import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MessageCircle,
  Save,
  Loader2,
  Bold,
  Italic,
  Underline,
  List,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import posthog from 'posthog-js';
import { useAuth } from '../contexts/AuthContext';
import { supabase, database } from '../supabase';
import { SHOW_QUIZ_FEATURES } from '../config/features';
import './Notes.css';

const RAILWAY_BASE = 'https://celebrated-charisma-production-5701.up.railway.app';

const MAX_NOTE_CONTEXT_CHARS = 12000;

/** Real RAG document name only — never use note title (backend returns "no document"). */
function getYoutubeDocumentName(note) {
  if (!note?.summary || typeof note.summary !== 'object') return null;
  const s = note.summary;
  const name = (s.source_document_name || s._rag_study_source_document_name || '').trim();
  return name || null;
}

function truncateContext(text) {
  if (!text || text.length <= MAX_NOTE_CONTEXT_CHARS) return text || '';
  return `${text.slice(0, MAX_NOTE_CONTEXT_CHARS)}\n\n[…truncated for length]`;
}

function buildNotesKnowledge(note, overviewEdit, highlightsEdit) {
  const parts = [];
  if (note?.title) parts.push(`Title: ${note.title}`);
  if (overviewEdit?.trim()) parts.push(`Overview:\n${overviewEdit.trim()}`);
  const hl = (highlightsEdit || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  if (hl.length) parts.push(`Key highlights:\n- ${hl.join('\n- ')}`);
  const s = note?.summary && typeof note.summary === 'object' ? note.summary : {};
  if (Array.isArray(s.topics)) {
    for (const t of s.topics) {
      const title = t?.topic || t?.title || '';
      if (title) parts.push(`Topic: ${title}`);
      if (Array.isArray(t?.subtopics)) {
        t.subtopics.forEach((st) => {
          if (st) parts.push(`  • ${st}`);
        });
      }
    }
  }
  if (s.next_steps && typeof s.next_steps === 'object') {
    const ns = s.next_steps;
    if (Array.isArray(ns.topics) && ns.topics.length) {
      parts.push(`Topics to explore:\n- ${ns.topics.filter(Boolean).join('\n- ')}`);
    }
    if (Array.isArray(ns.questions) && ns.questions.length) {
      parts.push(`Questions to consider:\n- ${ns.questions.filter(Boolean).join('\n- ')}`);
    }
  }
  if (typeof s.note_text === 'string' && s.note_text.trim()) {
    parts.push(`Additional notes:\n${s.note_text.trim()}`);
  }
  if (note?.transcript_length) {
    parts.push(`(Transcript stored: ${note.transcript_length} characters.)`);
  }
  return parts.join('\n\n');
}

function storageChatKey(noteId) {
  return `rag_study_notes_chat_${noteId}`;
}

function storageNoteFeedbackKey(noteId) {
  return `note_feedback_v1_${noteId}`;
}

function cleanFlashcardText(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/^Flashcard \d+:\s*/g, '')
    .replace(/^\d+\.\s*\*\*Flashcard \d+\*\*\s*/g, '')
    .replace(/^\*\*Front:\*\*\s*/g, '')
    .replace(/^\*\*Back:\*\*\s*/g, '')
    .replace(/^Front:\s*/g, '')
    .replace(/^Back:\s*/g, '')
    .replace(/^\d+\.\s*/g, '')
    .trim();
}

function parseFlashcardsFromApi(data) {
  const raw = data?.flashcards;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((card, index) => ({
      id: `generated_${index}`,
      front: cleanFlashcardText(card.front || card.question || 'Front'),
      back: cleanFlashcardText(card.back || card.answer || 'Back'),
    }));
  }
  if (typeof raw === 'string') {
    const flashcardPattern =
      /(\d+)\.\s*(?:\*\*)?Front:\s*(.*?)\s*(?:\*\*)?Back:\s*(.*?)(?=\d+\.|$)/gis;
    const matches = [...raw.matchAll(flashcardPattern)];
    if (matches.length > 0) {
      return matches.map((match, index) => ({
        id: `generated_${index}`,
        front: cleanFlashcardText(match[2]?.trim()),
        back: cleanFlashcardText(match[3]?.trim()),
      }));
    }
  }
  return [];
}

function parseQuizFromRaw(rawContent) {
  if (!rawContent || typeof rawContent !== 'string') return [];
  const questionPattern =
    /\*\*Question\s*(\d+):\*\*\s*(.*?)\s*A\)\s*(.*?)\s*B\)\s*(.*?)\s*C\)\s*(.*?)\s*D\)\s*(.*?)\s*\*\*Correct Answer:\*\*\s*[A-D]\)\s*(.*?)\s*\*\*Explanation:\*\*\s*(.*?)(?=\*\*Question\s*\d+:|$)/gis;
  const matches = [...rawContent.matchAll(questionPattern)];
  if (matches.length === 0) return [];
  return matches.map((m, i) => ({
    id: `generated_${i}`,
    question: (m[2] || '').trim(),
    options: [m[3], m[4], m[5], m[6]].map((x) => (x || '').trim()),
    correct_answer: 0,
    explanation: (m[8] || m[7] || '').trim(),
  }));
}

const Notes = () => {
  const { uid, noteId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [overviewEdit, setOverviewEdit] = useState('');
  const [highlightsEdit, setHighlightsEdit] = useState('');
  const [scratchpadHtml, setScratchpadHtml] = useState('');
  const [savingSummary, setSavingSummary] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const [quizBusy, setQuizBusy] = useState(false);
  const [flashBusy, setFlashBusy] = useState(false);

  const [showRightPanel, setShowRightPanel] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [noteFeedbackChoice, setNoteFeedbackChoice] = useState(null);

  const scratchRef = useRef(null);

  const youtubeDocName = note ? getYoutubeDocumentName(note) : null;
  const hasRagDocument = Boolean(youtubeDocName);
  const saveDocumentName = youtubeDocName || (note?.id ? `note:${note.id}` : null);

  useEffect(() => {
    const fetchNote = async () => {
      try {
        setLoading(true);

        const { data: supabaseUser, error: userError } = await supabase
          .from('users')
          .select('id')
          .eq('firebase_uid', user?.uid)
          .single();

        if (userError || !supabaseUser) {
          throw new Error('User not found');
        }

        const { data, error: fetchError } = await supabase
          .from('notes')
          .select('*')
          .eq('id', noteId)
          .eq('user_id', supabaseUser.id)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error('Note not found');
        }

        setNote(data);
        const sum = data.summary && typeof data.summary === 'object' ? data.summary : {};
        setOverviewEdit(typeof sum.overview === 'string' ? sum.overview : '');
        setHighlightsEdit(
          Array.isArray(sum.highlights) ? sum.highlights.join('\n') : ''
        );
        setScratchpadHtml(
          typeof sum.user_notes_html === 'string' ? sum.user_notes_html : ''
        );
      } catch (err) {
        console.error('Error fetching note:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (user && noteId) {
      fetchNote();
    }
  }, [user, noteId]);

  useEffect(() => {
    if (!noteId) return;
    try {
      const v = localStorage.getItem(storageNoteFeedbackKey(noteId));
      setNoteFeedbackChoice(v === 'up' || v === 'down' ? v : null);
    } catch {
      setNoteFeedbackChoice(null);
    }
  }, [noteId]);

  const handleNoteFeedback = (sentiment) => {
    if (noteFeedbackChoice || !noteId) return;
    const key = sentiment === 'positive' ? 'up' : 'down';
    setNoteFeedbackChoice(key);
    try {
      localStorage.setItem(storageNoteFeedbackKey(noteId), key);
    } catch (e) {
      console.warn('Note feedback persist:', e);
    }
    try {
      posthog.capture('note_satisfaction_feedback', {
        sentiment: key,
        note_id: noteId,
      });
    } catch (e) {
      console.warn('Note feedback analytics:', e);
    }
  };

  const saveSummary = async () => {
    if (!note || !user) return;
    setSavingSummary(true);
    setSaveMessage(null);
    try {
      const highlights = highlightsEdit
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      const nextSummary = {
        ...(note.summary && typeof note.summary === 'object' ? note.summary : {}),
        overview: overviewEdit,
        highlights,
        user_notes_html: scratchpadHtml,
      };

      const { data: supabaseUser, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('firebase_uid', user.uid)
        .single();
      if (userError || !supabaseUser) throw new Error('User not found');

      const { error: upErr } = await supabase
        .from('notes')
        .update({
          summary: nextSummary,
          updated_at: new Date().toISOString(),
        })
        .eq('id', note.id)
        .eq('user_id', supabaseUser.id);

      if (upErr) throw upErr;

      setNote((prev) => (prev ? { ...prev, summary: nextSummary } : prev));
      setSaveMessage('Saved');
      setTimeout(() => setSaveMessage(null), 2500);
    } catch (e) {
      console.error(e);
      setSaveMessage(e.message || 'Save failed');
    } finally {
      setSavingSummary(false);
    }
  };

  const persistLocalChat = useCallback(
    (lines) => {
      if (!noteId || hasRagDocument) return;
      try {
        localStorage.setItem(storageChatKey(noteId), JSON.stringify(lines));
      } catch (e) {
        console.warn('Chat persist:', e);
      }
    },
    [noteId, hasRagDocument]
  );

  const fetchChatHistory = useCallback(async () => {
    if (hasRagDocument && youtubeDocName) {
      try {
        const url = `${RAILWAY_BASE}/chat_history?document_name=${encodeURIComponent(
          youtubeDocName
        )}`;
        const response = await fetch(url, { method: 'GET', credentials: 'include' });
        if (!response.ok) return;
        const data = await response.json();
        if (data && Array.isArray(data.chat_history)) {
          const lines = data.chat_history.flatMap((chat) => [
            { role: 'user', text: chat.question },
            { role: 'assistant', text: chat.answer },
          ]);
          setChatMessages(lines);
        }
      } catch (e) {
        console.warn('Chat history:', e);
      }
      return;
    }
    try {
      const raw = localStorage.getItem(storageChatKey(noteId));
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setChatMessages(parsed);
      } else {
        setChatMessages([]);
      }
    } catch (e) {
      setChatMessages([]);
    }
  }, [hasRagDocument, youtubeDocName, noteId]);

  useEffect(() => {
    if (showRightPanel) {
      fetchChatHistory();
    }
  }, [showRightPanel, fetchChatHistory]);

  const askNotesAssistant = async (question, documentName) => {
    const userId = localStorage.getItem('rag_user_id') || user?.uid;
    const body = { question, user_id: userId };
    if (documentName) body.document_name = documentName;
    const response = await fetch(`${RAILWAY_BASE}/ask_question`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.detail || data.message || 'Request failed');
    }
    return typeof data.answer === 'string' ? data.answer : '';
  };

  const sendChat = async () => {
    const q = chatInput.trim();
    if (!q || !note || !user) return;
    setChatLoading(true);
    setChatInput('');
    const nextLines = [...chatMessages, { role: 'user', text: q }];
    setChatMessages(nextLines);
    try {
      let answerText;
      if (hasRagDocument && youtubeDocName) {
        answerText = await askNotesAssistant(q, youtubeDocName);
      } else {
        const knowledge = truncateContext(
          buildNotesKnowledge(note, overviewEdit, highlightsEdit)
        );
        if (!knowledge.trim()) {
          answerText =
            'There is no summary text on this note yet. Add an overview or highlights on the left, then try again.';
        } else {
          const wrapped = `You are helping a student with their saved note. Answer using ONLY the information below when the question is about the material. If the answer is not in the material, say so clearly.\n\n---NOTE CONTENT---\n${knowledge}\n---END---\n\nStudent question:\n${q}`;
          answerText = await askNotesAssistant(wrapped, null);
        }
      }
      const withAnswer = [...nextLines, { role: 'assistant', text: answerText || '(No reply)' }];
      setChatMessages(withAnswer);
      if (!hasRagDocument) persistLocalChat(withAnswer);
    } catch (e) {
      const errLine = [...nextLines, { role: 'assistant', text: e.message || 'Network error' }];
      setChatMessages(errLine);
      if (!hasRagDocument) persistLocalChat(errLine);
    } finally {
      setChatLoading(false);
    }
  };

  const handleGenerateFlashcards = async () => {
    if (!user || !note || !saveDocumentName) return;
    const knowledge = truncateContext(
      buildNotesKnowledge(note, overviewEdit, highlightsEdit)
    );
    if (!hasRagDocument && !knowledge.trim()) {
      alert('Add some summary text on this note first, then generate flashcards from it.');
      return;
    }
    setFlashBusy(true);
    try {
      let parsed = [];
      if (hasRagDocument && youtubeDocName) {
        const userId = localStorage.getItem('rag_user_id') || user.uid;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        const response = await fetch(`${RAILWAY_BASE}/generate_flashcards`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            user_id: userId,
            document_name: youtubeDocName,
            count: 10,
          }),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const data = await response.json();
        if (!response.ok) {
          alert(data.detail || 'Flashcard generation failed');
          return;
        }
        parsed = parseFlashcardsFromApi(data);
      } else {
        const prompt = `Create exactly 10 study flashcards from the SOURCE below. Output ONLY flashcards in this format (one per line, no preamble):
1. Front: <short question> Back: <concise answer>
2. Front: ...

SOURCE:
${knowledge}`;
        const answer = await askNotesAssistant(prompt, null);
        parsed = parseFlashcardsFromApi({ flashcards: answer });
      }
      parsed = parsed.filter((c) => c.front && c.back && c.front.trim() && c.back.trim());
      if (parsed.length === 0) {
        alert('No flashcards could be parsed. Try again or add more detail to your summary.');
        return;
      }
      const supabaseUser = await database.getUserByFirebaseUid(user.uid);
      if (!supabaseUser) {
        alert('User record not found');
        return;
      }
      const saved = await database.saveFlashcards(supabaseUser.id, {
        document_name: saveDocumentName,
        flashcards: parsed,
      });
      if (saved?.id && user.uid) {
        navigate(`/study-flashcards/${user.uid}/${saved.id}`);
      } else {
        alert(`Saved ${parsed.length} flashcards. Open Study flashcards from your library.`);
      }
    } catch (e) {
      console.error(e);
      alert(e.name === 'AbortError' ? 'Request timed out.' : e.message || 'Error');
    } finally {
      setFlashBusy(false);
    }
  };

  const handleGenerateQuiz = async () => {
    if (!SHOW_QUIZ_FEATURES || !user || !note || !saveDocumentName) return;
    const knowledge = truncateContext(
      buildNotesKnowledge(note, overviewEdit, highlightsEdit)
    );
    if (!hasRagDocument && !knowledge.trim()) {
      alert('Add some summary text on this note first, then generate a quiz from it.');
      return;
    }
    setQuizBusy(true);
    try {
      let parsed = [];
      if (hasRagDocument && youtubeDocName) {
        const userId = localStorage.getItem('rag_user_id') || user.uid;
        const requestBody = {
          document_name: youtubeDocName,
          question_count: 5,
          difficulty: 'intermediate',
          style: 'engaging',
          question_types: ['multiple_choice'],
          length: 'medium',
          user_id: userId,
        };
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000);
        const response = await fetch(`${RAILWAY_BASE}/generate_quiz`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody),
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        const data = await response.json();
        if (!response.ok) {
          alert(data.detail || 'Quiz generation failed');
          return;
        }
        const raw = data.quiz;
        parsed = parseQuizFromRaw(typeof raw === 'string' ? raw : '');
      } else {
        const prompt = `Create a 5-question multiple-choice quiz from the SOURCE below. Use EXACTLY this pattern for each question (including labels):

**Question 1:** <question text>
A) <option>
B) <option>
C) <option>
D) <option>
**Correct Answer:** A) <correct option text>
**Explanation:** <short explanation>

Repeat for questions 2–5. Output nothing before **Question 1:**.

SOURCE:
${knowledge}`;
        const answer = await askNotesAssistant(prompt, null);
        parsed = parseQuizFromRaw(answer);
      }
      if (parsed.length === 0) {
        alert('Quiz could not be parsed. Try again or add more detail to your summary.');
        return;
      }
      const supabaseUser = await database.getUserByFirebaseUid(user.uid);
      if (!supabaseUser) {
        alert('User record not found');
        return;
      }
      await database.saveQuiz(supabaseUser.id, {
        document_name: saveDocumentName,
        quiz: parsed,
        question_count: parsed.length,
      });
      alert(`Saved quiz (${parsed.length} questions). Open it from your library or dashboard.`);
    } catch (e) {
      console.error(e);
      alert(e.name === 'AbortError' ? 'Request timed out.' : e.message || 'Error');
    } finally {
      setQuizBusy(false);
    }
  };

  const extractVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  useLayoutEffect(() => {
    if (!showRightPanel || !scratchRef.current) return;
    // Intentionally omit scratchpadHtml from deps: only hydrate when opening panel or switching notes.
    scratchRef.current.innerHTML = scratchpadHtml || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRightPanel, note?.id]);

  const runScratchFormat = (command) => (e) => {
    e.preventDefault();
    const el = scratchRef.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand(command, false);
    } catch (err) {
      console.warn(err);
    }
    setScratchpadHtml(el.innerHTML);
  };

  if (loading) {
    return (
      <div className="notes-page">
        <div className="notes-content">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading note...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="notes-page">
        <div className="notes-content">
          <div className="error-container">
            <h2>Note not found</h2>
            <p>
              {error ||
                'This note does not exist or you do not have permission to view it.'}
            </p>
            <button
              onClick={() => navigate(uid ? `/main/${uid}` : '/')}
              className="back-btn"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const videoId = note.url ? extractVideoId(note.url) : null;
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}` : null;

  return (
    <div className="notes-page">
      <div className={`notes-split-wrap ${showRightPanel ? 'is-open' : ''}`}>
        <div className="notes-content notes-split-main">
          <div className="notes-header">
            <h1 className="notes-title">{note.title}</h1>
          </div>

          <div className="notes-body">
            <div className={`video-section ${!embedUrl ? 'video-section--no-embed' : ''}`}>
              {embedUrl && (
                <div className="video-container">
                  <iframe
                    src={embedUrl}
                    title={note.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              )}
              <div className="notes-under-video-actions">
                {SHOW_QUIZ_FEATURES && (
                  <button
                    type="button"
                    className="notes-action-btn notes-action-btn-quiz"
                    disabled={quizBusy}
                    onClick={handleGenerateQuiz}
                  >
                    {quizBusy ? (
                      <>
                        <Loader2 className="notes-btn-icon spin" size={18} /> Generating…
                      </>
                    ) : (
                      'Generate quiz'
                    )}
                  </button>
                )}
                <button
                  type="button"
                  className="notes-action-btn"
                  disabled={flashBusy}
                  onClick={handleGenerateFlashcards}
                >
                  {flashBusy ? (
                    <>
                      <Loader2 className="notes-btn-icon spin" size={18} /> Generating…
                    </>
                  ) : (
                    'Flashcards'
                  )}
                </button>
                <button
                  type="button"
                  className={`notes-action-btn notes-action-btn-chat ${
                    showRightPanel ? 'active' : ''
                  }`}
                  onClick={() => setShowRightPanel((v) => !v)}
                >
                  <MessageCircle size={18} />
                  Chat
                </button>
              </div>
              <p className="notes-action-hint">
                {hasRagDocument
                  ? 'Quiz and flashcards use your indexed video document. Chat uses the same index.'
                  : 'Quiz, flashcards, and chat use the summary and topics on this page as context (no separate library document).'}
              </p>
            </div>

            {note.summary && (
              <div className="summary-section">
                <div className="summary-section-head">
                  <h2>Summary</h2>
                  <div className="summary-head-actions">
                    <button
                      type="button"
                      className="notes-save-summary-btn"
                      onClick={saveSummary}
                      disabled={savingSummary}
                    >
                      <Save size={18} />
                      {savingSummary ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
                {saveMessage && (
                  <p className={`notes-save-msg ${saveMessage === 'Saved' ? 'ok' : 'err'}`}>
                    {saveMessage}
                  </p>
                )}

                <div className="summary-edit-block">
                <label className="summary-edit-label" htmlFor="notes-overview">
                  Overview
                </label>
                <textarea
                  id="notes-overview"
                  className="notes-summary-textarea"
                  rows={6}
                  value={overviewEdit}
                  onChange={(e) => setOverviewEdit(e.target.value)}
                  placeholder="Write or edit the overview…"
                />
              </div>

              <div className="summary-edit-block">
                <label className="summary-edit-label" htmlFor="notes-highlights">
                  Key highlights (one per line)
                </label>
                <textarea
                  id="notes-highlights"
                  className="notes-summary-textarea"
                  rows={5}
                  value={highlightsEdit}
                  onChange={(e) => setHighlightsEdit(e.target.value)}
                  placeholder="One highlight per line"
                />
              </div>

              {note.summary.topics && note.summary.topics.length > 0 && (
                <div className="summary-block">
                  <h3>Topics covered</h3>
                  {note.summary.topics.map((topic, index) => (
                    <div key={index} className="topic-item">
                      <h4>{topic.topic}</h4>
                      {topic.subtopics && topic.subtopics.length > 0 && (
                        <ul>
                          {topic.subtopics.map((subtopic, subIndex) => (
                            <li key={subIndex}>{subtopic}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {note.summary.next_steps && (
                <div className="summary-block">
                  <h3>Next steps</h3>
                  {note.summary.next_steps.topics &&
                    note.summary.next_steps.topics.length > 0 && (
                      <div>
                        <h4>Topics to explore</h4>
                        <ul>
                          {note.summary.next_steps.topics.map((topic, index) => (
                            <li key={index}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  {note.summary.next_steps.questions &&
                    note.summary.next_steps.questions.length > 0 && (
                      <div>
                        <h4>Questions to consider</h4>
                        <ul>
                          {note.summary.next_steps.questions.map((question, index) => (
                            <li key={index}>{question}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                </div>
              )}

              </div>
            )}

            <div className="notes-feedback-card" role="region" aria-label="Note feedback">
              <div className="notes-feedback-row">
                <p className="notes-feedback-line">
                  <span className="notes-feedback-strong">Happy with this note?</span>
                  <span className="notes-feedback-muted"> Help us improve</span>
                </p>
                <div className="notes-feedback-actions">
                  {noteFeedbackChoice ? (
                    <p className="notes-feedback-thanks">Thanks for your feedback!</p>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="notes-feedback-btn notes-feedback-btn--down"
                        aria-label="Thumbs down — this note could be better"
                        onClick={() => handleNoteFeedback('negative')}
                      >
                        <ThumbsDown size={20} strokeWidth={2} aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="notes-feedback-btn notes-feedback-btn--up"
                        aria-label="Thumbs up — happy with this note"
                        onClick={() => handleNoteFeedback('positive')}
                      >
                        <ThumbsUp size={20} strokeWidth={2} aria-hidden />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {showRightPanel && (
          <aside className="notes-side-panel" aria-label="Chat and personal notes">
            <div className="notes-side-panel-head">
              <h3 className="notes-side-panel-title">Chat & notes</h3>
              <button
                type="button"
                className="notes-side-close"
                onClick={() => setShowRightPanel(false)}
                aria-label="Close panel"
              >
                ×
              </button>
            </div>

            <div className="notes-side-chat">
              <div className="notes-chat-messages">
                {chatMessages.length === 0 && !chatLoading && (
                  <p className="notes-chat-empty">
                    {hasRagDocument
                      ? 'Ask a question about this video.'
                      : 'Ask about this note — answers use your summary and topics on the left.'}
                  </p>
                )}
                {chatMessages.map((m, i) => (
                  <div
                    key={i}
                    className={`notes-chat-bubble ${m.role === 'user' ? 'user' : 'ai'}`}
                  >
                    {m.text}
                  </div>
                ))}
                {chatLoading && (
                  <div className="notes-chat-bubble ai">
                    <Loader2 className="notes-btn-icon spin" size={16} /> Thinking…
                  </div>
                )}
              </div>
              <div className="notes-chat-input-row">
                <input
                  type="text"
                  className="notes-chat-input"
                  placeholder={
                    hasRagDocument ? 'Ask about this video…' : 'Ask about this note…'
                  }
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && sendChat()}
                  disabled={chatLoading}
                />
                <button
                  type="button"
                  className="notes-chat-send"
                  onClick={sendChat}
                  disabled={chatLoading || !chatInput.trim()}
                >
                  Send
                </button>
              </div>
            </div>

            <div className="notes-scratch-section">
              <h4 className="notes-scratch-heading">Your notes</h4>
              <p className="notes-scratch-lead">
                Format text like a doc — saved with <strong>Save</strong> on the summary.
              </p>
              <div className="notes-scratch-toolbar">
                <button
                  type="button"
                  className="notes-scratch-tool"
                  onClick={runScratchFormat('bold')}
                  title="Bold"
                >
                  <Bold size={16} />
                </button>
                <button
                  type="button"
                  className="notes-scratch-tool"
                  onClick={runScratchFormat('italic')}
                  title="Italic"
                >
                  <Italic size={16} />
                </button>
                <button
                  type="button"
                  className="notes-scratch-tool"
                  onClick={runScratchFormat('underline')}
                  title="Underline"
                >
                  <Underline size={16} />
                </button>
                <button
                  type="button"
                  className="notes-scratch-tool"
                  onClick={runScratchFormat('insertUnorderedList')}
                  title="Bullet list"
                >
                  <List size={16} />
                </button>
              </div>
              <div
                ref={scratchRef}
                className="notes-scratch-editor"
                contentEditable
                suppressContentEditableWarning
                role="textbox"
                aria-multiline="true"
                onInput={(e) => setScratchpadHtml(e.currentTarget.innerHTML)}
              />
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default Notes;
